import type { IndustryData } from './IndustryPage';

export const industries: Record<string, IndustryData> = {
  restaurants: {
    slug: 'restaurants',
    icon: 'ri-restaurant-line',
    name: 'Restaurants & QSR',
    tagline: 'Built for Restaurants',
    headline: 'Deliver faster, delight diners with',
    headlineHighlight: 'AI-powered dispatch',
    description: 'Automate order-to-door delivery, reduce wait times, and collect 5-star reviews — all from one platform built for restaurants and quick-service chains.',
    heroImage: '/images/industries/restaurants-industry-hero.png',
    color: '#FF6B35',
    colorLight: '#FFF1EB',
    heroKeyPoints: [
      'Eliminate third-party delivery commissions',
      'AI dispatch matches orders to drivers in seconds',
      'Branded tracking pages with your restaurant logo',
      'Automated Google review collection after delivery',
    ],
    stats: [
      { value: '24 min', label: 'Avg delivery time' },
      { value: '40%', label: 'Faster dispatch' },
      { value: '99.99%', label: 'Platform uptime' },
    ],
    painPoints: [
      { icon: 'ri-timer-line', title: 'Slow manual dispatch', description: 'Orders pile up while managers manually assign drivers, causing cold food and angry customers.' },
      { icon: 'ri-money-dollar-circle-line', title: 'High commission fees', description: 'Third-party platforms take 20-30% per order, eroding your margins on every delivery.' },
      { icon: 'ri-emotion-sad-line', title: 'Poor delivery experience', description: 'Customers can\'t track orders, call repeatedly for ETAs, and leave negative reviews.' },
    ],
    features: [
      { icon: 'ri-brain-line', title: 'AI Auto Dispatch', description: 'Instantly match orders to the nearest available driver based on location and kitchen prep time.', tag: 'AI' },
      { icon: 'ri-route-line', title: 'Multi-stop routing', description: 'Batch multiple orders per driver run with optimized routes to maximize delivery density.' },
      { icon: 'ri-map-pin-user-line', title: 'Branded tracking', description: 'Your logo, your colors — a tracking page that builds your brand, not someone else\'s.' },
      { icon: 'ri-star-line', title: 'Review collection', description: 'Automatically request Google reviews from happy customers to boost your online reputation.' },
      { icon: 'ri-time-line', title: 'Predictive ETA', description: 'AI estimates delivery time factoring in prep time, traffic, and distance — accurate to the minute.', tag: 'AI' },
      { icon: 'ri-smartphone-line', title: 'Driver app', description: 'Simple app with turn-by-turn navigation, one-tap POD, and in-app chat with dispatch.' },
      { icon: 'ri-bar-chart-box-line', title: 'Performance analytics', description: 'Track delivery times, driver performance, and customer satisfaction in real-time dashboards.' },
      { icon: 'ri-plug-line', title: 'POS integrations', description: 'Connect with Toast, Square, Lightspeed, and 50+ other platforms in minutes.' },
    ],
    testimonial: {
      quote: 'We cut delivery times by 35% and eliminated third-party commissions entirely. Geofleet paid for itself in the first week.',
      role: 'Operations Director',
      metric: '35% faster deliveries',
    },
    useCases: ['Single-location restaurants', 'Multi-location QSR chains', 'Cloud kitchens & ghost kitchens', 'Pizza delivery operations', 'Catering & meal prep services', 'Food truck fleets'],
  },

  pharmacies: {
    slug: 'pharmacies',
    icon: 'ri-capsule-line',
    name: 'Pharmacies',
    tagline: 'Built for Pharmacies',
    headline: 'Reliable prescription delivery with',
    headlineHighlight: 'full chain of custody',
    description: 'Ensure safe, compliant, and on-time medication delivery with ID verification, temperature logging, and real-time tracking for every order.',
    heroImage: '/images/industries/pharmacies-industry-hero.png',
    color: '#00A67E',
    colorLight: '#E6F9F3',
    heroKeyPoints: [
      'SOC 2 and ISO 27001 certified, with encrypted data',
      'Patient ID verification before handoff',
      'Temperature monitoring for cold-chain meds',
      'Recurring prescription auto-scheduling',
    ],
    stats: [
      { value: '99.8%', label: 'On-time delivery' },
      { value: '100%', label: 'ID verified' },
      { value: '45 min', label: 'Avg fulfillment' },
    ],
    painPoints: [
      { icon: 'ri-shield-cross-line', title: 'Compliance risks', description: 'Delivering controlled substances without proper verification exposes you to regulatory penalties.' },
      { icon: 'ri-time-line', title: 'Missed delivery windows', description: 'Patients depend on timely medication — late deliveries impact health outcomes and trust.' },
      { icon: 'ri-file-unknow-line', title: 'No delivery proof', description: 'Without verified proof of delivery, disputes and chargebacks become a recurring headache.' },
    ],
    features: [
      { icon: 'ri-shield-check-line', title: 'ID verification', description: 'Drivers verify patient identity with photo ID and signature capture before handoff.' },
      { icon: 'ri-temp-cold-line', title: 'Temperature tracking', description: 'Monitor cold-chain compliance for temperature-sensitive medications in transit.' },
      { icon: 'ri-brain-line', title: 'AI scheduling', description: 'Automatically batch and schedule recurring prescriptions for efficient daily routes.', tag: 'AI' },
      { icon: 'ri-lock-line', title: 'Certified security', description: 'SOC 2 and ISO 27001 certified platform, with data encrypted at rest and in transit.' },
      { icon: 'ri-map-pin-user-line', title: 'Patient tracking', description: 'Patients receive real-time SMS updates with driver location and accurate ETA.' },
      { icon: 'ri-camera-line', title: 'Photo + signature POD', description: 'Complete chain-of-custody documentation with timestamped photos and e-signatures.' },
      { icon: 'ri-repeat-line', title: 'Recurring deliveries', description: 'Set up weekly or monthly auto-scheduled deliveries for chronic medication patients.' },
      { icon: 'ri-bar-chart-box-line', title: 'Compliance reports', description: 'Audit-ready reports for every delivery including timestamps, signatures, and ID checks.' },
    ],
    testimonial: {
      quote: 'Our patients now receive medications within 45 minutes. The ID verification feature gave us the compliance confidence we needed to scale.',
      role: 'Director of Operations',
      metric: '99.8% on-time rate',
    },
    useCases: ['Independent pharmacies', 'Pharmacy chains', 'Hospital outpatient delivery', 'Specialty medication delivery', 'Compounding pharmacies', 'Long-term care facilities'],
  },

  retail: {
    slug: 'retail',
    icon: 'ri-store-2-line',
    name: 'Retail',
    tagline: 'Built for Retail',
    headline: 'Same-day retail delivery at',
    headlineHighlight: 'scale with AI',
    description: 'Offer same-day and next-day delivery from every store location, turning your physical footprint into a competitive delivery advantage.',
    heroImage: '/images/industries/retail-industry-hero.png',
    color: '#8B5CF6',
    colorLight: '#F3EEFF',
    heroKeyPoints: [
      'Fulfill from the nearest store location',
      'AI batches nearby orders for efficient runs',
      'Barcode scanning for 100% order accuracy',
      'Omnichannel — Shopify, Square, POS sync',
    ],
    stats: [
      { value: '2hr', label: 'Same-day delivery' },
      { value: '30%', label: 'Lower costs' },
      { value: '50+', label: 'Integrations' },
    ],
    painPoints: [
      { icon: 'ri-shopping-bag-line', title: 'Rising delivery expectations', description: 'Customers expect same-day delivery but your logistics can\'t keep up with e-commerce giants.' },
      { icon: 'ri-money-dollar-circle-line', title: 'Expensive last-mile', description: 'Last-mile delivery accounts for 53% of total shipping cost, crushing retail margins.' },
      { icon: 'ri-store-line', title: 'Disconnected channels', description: 'Online orders, in-store pickups, and deliveries run on separate, siloed systems.' },
    ],
    features: [
      { icon: 'ri-brain-line', title: 'AI order batching', description: 'Automatically group nearby orders for efficient multi-stop delivery runs.', tag: 'AI' },
      { icon: 'ri-store-2-line', title: 'Store-as-hub', description: 'Fulfill deliveries from the nearest store location, reducing distance and delivery time.' },
      { icon: 'ri-route-line', title: 'Dynamic routing', description: 'Routes adjust in real-time based on traffic, new orders, and driver availability.', tag: 'AI' },
      { icon: 'ri-map-pin-user-line', title: 'Branded experience', description: 'Custom tracking pages with your branding, promotions, and loyalty program links.' },
      { icon: 'ri-barcode-line', title: 'Barcode scanning', description: 'Drivers scan items at pickup and delivery for 100% order accuracy verification.' },
      { icon: 'ri-camera-line', title: 'Visual proof', description: 'Photo proof of delivery for every package — reducing disputes and chargebacks.' },
      { icon: 'ri-plug-line', title: 'Omnichannel sync', description: 'Connect Shopify, WooCommerce, Square, and your POS for unified order management.' },
      { icon: 'ri-line-chart-line', title: 'Demand forecasting', description: 'AI predicts delivery demand by location and time, so you staff drivers accordingly.', tag: 'AI' },
    ],
    testimonial: {
      quote: 'We launched same-day delivery across 40 stores in 2 weeks. Geofleet\'s store-as-hub model cut our delivery costs by 30%.',
      role: 'VP of E-commerce',
      metric: '40 stores in 2 weeks',
    },
    useCases: ['Department stores', 'Specialty retail', 'Home goods & furniture', 'Electronics retailers', 'Pet supply stores', 'Sporting goods'],
  },

  ecommerce: {
    slug: 'ecommerce',
    icon: 'ri-shopping-cart-line',
    name: 'E-commerce',
    tagline: 'Built for E-commerce',
    headline: 'Scale your e-commerce delivery with',
    headlineHighlight: 'AI-powered logistics',
    description: 'From checkout to doorstep — automate fulfillment, optimize carrier selection, and provide Amazon-level tracking for every order.',
    heroImage: '/images/industries/ecommerce-industry-hero.png',
    color: '#1769ff',
    colorLight: '#eef5ff',
    heroKeyPoints: [
      'Process 15,000+ orders per day without errors',
      'AI selects the best carrier per order automatically',
      'Branded tracking reduces WISMO calls by 60%',
      'One-click Shopify, WooCommerce integration',
    ],
    stats: [
      { value: '10x', label: 'Throughput capacity' },
      { value: '95%', label: 'On-time delivery' },
      { value: '60%', label: 'Fewer WISMO calls' },
    ],
    painPoints: [
      { icon: 'ri-customer-service-line', title: '"Where is my order?" calls', description: 'Support teams spend 40% of their time answering delivery status inquiries.' },
      { icon: 'ri-scales-line', title: 'Scaling bottlenecks', description: 'Manual dispatch processes break down during flash sales and peak season.' },
      { icon: 'ri-truck-line', title: 'Carrier complexity', description: 'Managing multiple carriers with different APIs, pricing, and SLAs is operational chaos.' },
    ],
    features: [
      { icon: 'ri-robot-2-line', title: 'AI carrier selection', description: 'Automatically choose the best carrier for each order based on cost, speed, and reliability.', tag: 'AI' },
      { icon: 'ri-brain-line', title: 'Smart dispatch', description: 'AI processes thousands of orders per minute, batching and routing them optimally.', tag: 'AI' },
      { icon: 'ri-notification-3-line', title: 'Proactive updates', description: 'Automated SMS, email, and WhatsApp notifications at every delivery milestone.' },
      { icon: 'ri-map-pin-user-line', title: 'Branded tracking', description: 'Tracking pages with your brand, cross-sell recommendations, and return options.' },
      { icon: 'ri-refund-2-line', title: 'Auto-refunds', description: 'AI detects delivery failures and auto-initiates refund claims with carriers.' },
      { icon: 'ri-plug-line', title: 'Platform integrations', description: 'One-click connect with Shopify, WooCommerce, BigCommerce, and Squarespace.' },
      { icon: 'ri-line-chart-line', title: 'Shipping analytics', description: 'Cost per delivery, carrier performance, and delivery time benchmarks in one dashboard.' },
      { icon: 'ri-code-s-slash-line', title: 'API & webhooks', description: 'Full REST API for custom integrations, order tracking, and webhook-driven automation.' },
    ],
    testimonial: {
      quote: 'During our Black Friday sale, Geofleet processed 15,000 orders in a single day without a single dispatch error. Game-changer.',
      role: 'Head of Fulfillment',
      metric: '15K orders/day',
    },
    useCases: ['D2C brands', 'Marketplace sellers', 'Subscription box companies', 'Flash sale platforms', 'Dropshipping businesses', 'Multi-channel sellers'],
  },

  couriers: {
    slug: 'couriers',
    icon: 'ri-truck-line',
    name: 'Couriers & Logistics',
    tagline: 'Built for Couriers',
    headline: 'Run your courier fleet with',
    headlineHighlight: 'AI-powered precision',
    description: 'From parcel pickups to last-mile delivery — manage drivers, optimize routes, and maintain full visibility across your entire courier operation.',
    heroImage: '/images/industries/couriers-industry-hero.png',
    color: '#0EA5E9',
    colorLight: '#E8F6FE',
    heroKeyPoints: [
      'Scale from 50 to 500 drivers effortlessly',
      'Fleet-wide route optimization in seconds',
      'Auto-calculate driver payroll & bonuses',
      'Enterprise API for white-label delivery',
    ],
    stats: [
      { value: '500+', label: 'Drivers managed' },
      { value: '30%', label: 'Route efficiency gain' },
      { value: '99.99%', label: 'Platform uptime' },
    ],
    painPoints: [
      { icon: 'ri-group-line', title: 'Driver management chaos', description: 'Coordinating dozens or hundreds of drivers across shifts and zones is a full-time headache.' },
      { icon: 'ri-gas-station-line', title: 'Rising fuel costs', description: 'Inefficient routes burn fuel and money — every unnecessary kilometer hits your bottom line.' },
      { icon: 'ri-eye-off-line', title: 'Lack of visibility', description: 'Without real-time tracking, you\'re blind to delays, idle time, and delivery exceptions.' },
    ],
    features: [
      { icon: 'ri-brain-line', title: 'AI zone dispatch', description: 'Automatically assign drivers to zones and orders based on real-time capacity and proximity.', tag: 'AI' },
      { icon: 'ri-route-line', title: 'Fleet route optimizer', description: 'Optimize routes for your entire fleet simultaneously, not just individual drivers.', tag: 'AI' },
      { icon: 'ri-dashboard-3-line', title: 'Control tower', description: 'Live map view of your entire fleet with alerts for delays, exceptions, and SLA breaches.' },
      { icon: 'ri-money-dollar-circle-line', title: 'Driver payroll', description: 'Auto-calculate driver earnings based on deliveries, distance, bonuses, and deductions.' },
      { icon: 'ri-barcode-line', title: 'Scan & sort', description: 'Barcode scanning for package intake, sorting, and delivery verification.' },
      { icon: 'ri-chat-voice-line', title: 'Dispatch comms', description: 'In-app messaging between dispatchers and drivers with masked customer phone numbers.' },
      { icon: 'ri-line-chart-line', title: 'Fleet analytics', description: 'Driver scorecards, delivery density maps, and cost-per-delivery breakdowns.', tag: 'AI' },
      { icon: 'ri-code-s-slash-line', title: 'Enterprise API', description: 'White-label API for client integrations, order injection, and webhook notifications.' },
    ],
    testimonial: {
      quote: 'Geofleet helped us go from 50 to 500 drivers in 6 months. The AI dispatch alone saved us 4 full-time dispatchers.',
      role: 'CEO',
      metric: '10x fleet growth',
    },
    useCases: ['Parcel delivery companies', 'Same-day courier services', 'Medical courier fleets', 'Document delivery', 'B2B logistics providers', 'White-glove delivery services'],
  },

  laundry: {
    slug: 'laundry',
    icon: 'ri-t-shirt-line',
    name: 'Laundry & Dry Cleaning',
    tagline: 'Built for Laundry',
    headline: 'Pickup & delivery for laundry,',
    headlineHighlight: 'fully automated',
    description: 'Manage pickups, processing, and delivery in one platform. Route optimization and scheduling built for the unique needs of laundry operations.',
    heroImage: '/images/industries/laundry-industry-hero.png',
    color: '#EC4899',
    colorLight: '#FDF2F8',
    heroKeyPoints: [
      'Round-trip routing for pickup + delivery',
      'Barcode-based garment tracking end-to-end',
      'Recurring weekly schedules per customer',
      'Condition photos to prevent disputes',
    ],
    stats: [
      { value: '2x', label: 'More pickups/day' },
      { value: '98%', label: 'On-time returns' },
      { value: '25%', label: 'Lower op costs' },
    ],
    painPoints: [
      { icon: 'ri-repeat-line', title: 'Complex two-way logistics', description: 'Every order requires a pickup AND a delivery — doubling your routing complexity.' },
      { icon: 'ri-calendar-line', title: 'Scheduling nightmares', description: 'Coordinating customer availability windows with driver schedules is operationally heavy.' },
      { icon: 'ri-exchange-line', title: 'Lost item disputes', description: 'Without proper tracking, garment mix-ups and lost item claims drain time and money.' },
    ],
    features: [
      { icon: 'ri-brain-line', title: 'AI pickup scheduling', description: 'Automatically batch pickups by zone and time window for maximum route efficiency.', tag: 'AI' },
      { icon: 'ri-route-line', title: 'Round-trip routing', description: 'Optimize both pickup and delivery legs in a single route plan.' },
      { icon: 'ri-barcode-line', title: 'Garment tracking', description: 'Barcode-based item tracking from pickup to processing to delivery.' },
      { icon: 'ri-repeat-line', title: 'Recurring schedules', description: 'Set up weekly pickup/delivery schedules for regular customers.' },
      { icon: 'ri-map-pin-user-line', title: 'Customer tracking', description: 'Customers track both pickup and delivery status in real-time.' },
      { icon: 'ri-camera-line', title: 'Condition photos', description: 'Drivers photo-document garment condition at pickup to prevent disputes.' },
      { icon: 'ri-notification-3-line', title: 'Smart reminders', description: 'Automated reminders for upcoming pickups and ready-for-delivery notifications.' },
      { icon: 'ri-line-chart-line', title: 'Ops dashboard', description: 'Track items in processing, pending pickups, and delivery completion rates.' },
    ],
    testimonial: {
      quote: 'We doubled our daily pickup capacity without adding drivers. The round-trip routing alone saves us 3 hours of planning every day.',
      role: 'Founder',
      metric: '2x pickup capacity',
    },
    useCases: ['Laundry delivery services', 'Dry cleaning chains', 'Wash-and-fold services', 'Corporate uniform services', 'Linen rental companies', 'Alterations pickup/delivery'],
  },

  flowers: {
    slug: 'flowers',
    icon: 'ri-flower-line',
    name: 'Flowers & Gifts',
    tagline: 'Built for Flowers & Gifts',
    headline: 'Time-sensitive deliveries,',
    headlineHighlight: 'perfectly timed',
    description: 'Ensure every bouquet and gift arrives fresh and on time with precision scheduling, real-time tracking, and photo proof of delivery.',
    heroImage: '/images/industries/flowers-industry-hero.png',
    color: '#F59E0B',
    colorLight: '#FFFBEB',
    heroKeyPoints: [
      'Schedule within specific time slots',
      'AI auto-scales drivers for holiday surges',
      'Doorstep photo sent to sender instantly',
      'Gift messages on branded tracking pages',
    ],
    stats: [
      { value: '99%', label: 'On-time delivery' },
      { value: '3x', label: 'Peak day capacity' },
      { value: '90%', label: 'Photo POD rate' },
    ],
    painPoints: [
      { icon: 'ri-timer-line', title: 'Time-critical deliveries', description: 'Late flowers for a birthday or anniversary means a ruined experience — and a lost customer.' },
      { icon: 'ri-calendar-event-line', title: 'Holiday rush chaos', description: 'Valentine\'s Day and Mother\'s Day volumes spike 5-10x, overwhelming manual dispatch.' },
      { icon: 'ri-question-line', title: 'No delivery confirmation', description: 'Senders never know if the gift was actually received, leading to anxiety and support calls.' },
    ],
    features: [
      { icon: 'ri-time-line', title: 'Time-window delivery', description: 'Schedule deliveries within specific time slots to ensure surprise timing is perfect.' },
      { icon: 'ri-brain-line', title: 'AI surge planning', description: 'Predict demand spikes for holidays and auto-scale driver allocation.', tag: 'AI' },
      { icon: 'ri-camera-line', title: 'Doorstep photo POD', description: 'Drivers photograph the delivery at the door — senders get instant visual confirmation.' },
      { icon: 'ri-gift-line', title: 'Gift messaging', description: 'Include personalized messages on the tracking page visible to recipients.' },
      { icon: 'ri-route-line', title: 'Priority routing', description: 'Time-sensitive orders are prioritized in route optimization.', tag: 'AI' },
      { icon: 'ri-notification-3-line', title: 'Sender notifications', description: 'Real-time delivery updates sent to both sender and recipient.' },
      { icon: 'ri-map-pin-user-line', title: 'Live tracking', description: 'Recipients can track the surprise delivery in real-time on branded pages.' },
      { icon: 'ri-bar-chart-box-line', title: 'Seasonal analytics', description: 'Plan staffing and inventory based on historical delivery volume data.' },
    ],
    testimonial: {
      quote: 'Last Valentine\'s Day we delivered 3x our normal volume with a 99% on-time rate. The photo POD feature eliminated "was it delivered?" calls.',
      role: 'Owner',
      metric: '3x Valentine\'s volume',
    },
    useCases: ['Flower shops', 'Gift basket companies', 'Balloon delivery', 'Subscription flower services', 'Event florists', 'Corporate gifting'],
  },

  water: {
    slug: 'water',
    icon: 'ri-drop-line',
    name: 'Water Delivery',
    tagline: 'Built for Water Delivery',
    headline: 'Recurring water delivery,',
    headlineHighlight: 'automatically optimized',
    description: 'Manage recurring subscriptions, optimize bottle delivery routes, and track inventory — purpose-built for water delivery operations.',
    heroImage: '/images/industries/water-industry-hero.png',
    color: '#06B6D4',
    colorLight: '#ECFEFF',
    heroKeyPoints: [
      'Auto-schedule weekly/monthly subscriptions',
      'Bottle delivered + empties collected tracking',
      'COD collection with digital receipts',
      'Customer self-service portal',
    ],
    stats: [
      { value: '40%', label: 'Route efficiency' },
      { value: '500+', label: 'Stops per day' },
      { value: '99%', label: 'Delivery accuracy' },
    ],
    painPoints: [
      { icon: 'ri-repeat-line', title: 'Complex recurring schedules', description: 'Managing weekly, bi-weekly, and monthly deliveries across hundreds of customers is chaos.' },
      { icon: 'ri-route-line', title: 'Inefficient routes', description: 'Drivers criss-cross neighborhoods because routes aren\'t optimized by geography.' },
      { icon: 'ri-exchange-line', title: 'Bottle tracking gaps', description: 'Losing track of empty bottle returns means lost assets and customer disputes.' },
    ],
    features: [
      { icon: 'ri-repeat-line', title: 'Subscription management', description: 'Auto-schedule recurring deliveries based on customer subscription frequency.' },
      { icon: 'ri-brain-line', title: 'AI route clustering', description: 'Group nearby deliveries by day and optimize routes for dense, efficient coverage.', tag: 'AI' },
      { icon: 'ri-exchange-line', title: 'Bottle tracking', description: 'Track bottles delivered and empties collected per customer per visit.' },
      { icon: 'ri-money-dollar-circle-line', title: 'COD collection', description: 'Drivers collect payments on delivery with digital receipts and cash reconciliation.' },
      { icon: 'ri-map-pin-user-line', title: 'Customer portal', description: 'Customers manage their own schedules, skip deliveries, or add extra bottles.' },
      { icon: 'ri-notification-3-line', title: 'Delivery reminders', description: 'Automated reminders sent to customers before scheduled delivery day.' },
      { icon: 'ri-camera-line', title: 'Delivery proof', description: 'Photo evidence of bottles placed at the designated delivery spot.' },
      { icon: 'ri-bar-chart-box-line', title: 'Inventory analytics', description: 'Track bottle inventory, return rates, and predict refill demand by area.', tag: 'AI' },
    ],
    testimonial: {
      quote: 'Route optimization alone saved us 40% on fuel. The subscription management means we never miss a scheduled delivery.',
      role: 'Operations Manager',
      metric: '40% fuel savings',
    },
    useCases: ['Bottled water delivery', 'Water purifier services', 'Bulk water tanker operations', 'Office water cooler services', 'Subscription beverage delivery', 'Ice delivery services'],
  },

  fuel: {
    slug: 'fuel',
    icon: 'ri-gas-station-line',
    name: 'Fuel & Gas Delivery',
    tagline: 'Built for Fuel Delivery',
    headline: 'On-demand fuel delivery with',
    headlineHighlight: 'safety-first logistics',
    description: 'Manage hazmat-compliant fuel delivery operations with real-time tracking, safety checklists, and AI-optimized routing for your tanker fleet.',
    heroImage: '/images/industries/fuel-industry-hero.png',
    color: '#EF4444',
    colorLight: '#FEF2F2',
    heroKeyPoints: [
      'Digital pre-delivery safety checklists',
      'Weight-restricted tanker route optimization',
      'Digital delivery tickets replace paper',
      'Fuel volume reconciliation per delivery',
    ],
    stats: [
      { value: '100%', label: 'Safety compliance' },
      { value: '25%', label: 'Route optimization' },
      { value: '15 min', label: 'Avg dispatch time' },
    ],
    painPoints: [
      { icon: 'ri-alarm-warning-line', title: 'Safety compliance', description: 'Fuel delivery requires strict hazmat protocols — any lapse risks lives and licenses.' },
      { icon: 'ri-route-line', title: 'Tanker routing complexity', description: 'Heavy vehicles with restricted road access need specialized route planning.' },
      { icon: 'ri-file-list-line', title: 'Paper-based processes', description: 'Delivery tickets, safety checklists, and meter readings are still done on paper.' },
    ],
    features: [
      { icon: 'ri-shield-check-line', title: 'Safety checklists', description: 'Digital pre-delivery safety checklists that drivers must complete before dispensing.' },
      { icon: 'ri-brain-line', title: 'AI tanker routing', description: 'Route optimization considering vehicle weight restrictions, road limits, and fuel capacity.', tag: 'AI' },
      { icon: 'ri-dashboard-3-line', title: 'Fleet monitoring', description: 'Real-time GPS tracking with speed alerts, geofencing, and idle time detection.' },
      { icon: 'ri-file-list-line', title: 'Digital delivery tickets', description: 'Replace paper tickets with digital records including meter readings and volumes.' },
      { icon: 'ri-drop-line', title: 'Volume tracking', description: 'Track fuel dispensed per delivery with automated reconciliation against tank levels.' },
      { icon: 'ri-camera-line', title: 'Compliance photos', description: 'Photo documentation of safety setup, meter readings, and delivery completion.' },
      { icon: 'ri-calendar-line', title: 'Scheduled deliveries', description: 'Auto-schedule refueling based on consumption patterns and tank level thresholds.', tag: 'AI' },
      { icon: 'ri-bar-chart-box-line', title: 'Consumption analytics', description: 'Track delivery volumes, costs, and consumption trends across all customers.' },
    ],
    testimonial: {
      quote: 'Going digital with Geofleet eliminated 100% of our paper tickets and cut dispatch time from 45 minutes to 15 minutes.',
      role: 'Fleet Manager',
      metric: '100% paperless',
    },
    useCases: ['Diesel delivery companies', 'On-site refueling services', 'Propane & LPG delivery', 'Generator refueling', 'Fleet fueling services', 'Heating oil delivery'],
  },

  manufacturing: {
    slug: 'manufacturing',
    icon: 'ri-building-2-line',
    name: 'Manufacturing',
    tagline: 'Built for Manufacturing',
    headline: 'Supply chain last-mile,',
    headlineHighlight: 'delivered with precision',
    description: 'Manage inbound and outbound shipments between factories, warehouses, and customers with real-time visibility and AI-optimized scheduling.',
    heroImage: '/images/industries/manufacturing-industry-hero.png',
    color: '#6366F1',
    colorLight: '#EEF2FF',
    heroKeyPoints: [
      'Sync deliveries with production schedules',
      'AI load planning by weight, volume & sequence',
      'Pallet/SKU barcode scanning at every point',
      'ERP integration — SAP, Oracle, and more',
    ],
    stats: [
      { value: '99.5%', label: 'Delivery accuracy' },
      { value: '20%', label: 'Cost reduction' },
      { value: '24/7', label: 'Fleet visibility' },
    ],
    painPoints: [
      { icon: 'ri-time-line', title: 'Production schedule dependency', description: 'Deliveries must align with production schedules — any misalignment stops the line.' },
      { icon: 'ri-truck-line', title: 'Multi-vehicle complexity', description: 'Managing trucks, vans, and specialized vehicles with different capacities and requirements.' },
      { icon: 'ri-file-unknow-line', title: 'Shipment visibility gaps', description: 'Once goods leave the warehouse, you lose visibility until the customer confirms receipt.' },
    ],
    features: [
      { icon: 'ri-brain-line', title: 'AI load planning', description: 'Optimize vehicle loading based on weight, volume, delivery sequence, and vehicle type.', tag: 'AI' },
      { icon: 'ri-calendar-line', title: 'Production sync', description: 'Schedule pickups aligned with production output for just-in-time delivery.' },
      { icon: 'ri-dashboard-3-line', title: 'Shipment visibility', description: 'Real-time tracking from factory floor to customer dock with milestone updates.' },
      { icon: 'ri-barcode-line', title: 'Pallet/SKU scanning', description: 'Scan-based verification at pickup, each transfer point, and final delivery.' },
      { icon: 'ri-route-line', title: 'Multi-stop optimization', description: 'Plan delivery runs across multiple warehouses and customer sites efficiently.', tag: 'AI' },
      { icon: 'ri-camera-line', title: 'Damage documentation', description: 'Photo documentation at pickup and delivery for damage claim prevention.' },
      { icon: 'ri-code-s-slash-line', title: 'ERP integration', description: 'Connect with SAP, Oracle, and other ERP systems for automated order flow.' },
      { icon: 'ri-line-chart-line', title: 'Supply chain analytics', description: 'End-to-end delivery performance metrics across your supply chain network.' },
    ],
    testimonial: {
      quote: 'Geofleet syncs perfectly with our production schedule. We reduced delivery exceptions by 80% and cut logistics costs by 20%.',
      role: 'Supply Chain Director',
      metric: '80% fewer exceptions',
    },
    useCases: ['Automotive parts delivery', 'Electronics distribution', 'Raw materials transport', 'Warehouse-to-store delivery', 'Spare parts logistics', 'Finished goods distribution'],
  },

  grocery: {
    slug: 'grocery',
    icon: 'ri-shopping-basket-2-line',
    name: 'Grocery & Supermarkets',
    tagline: 'Built for Grocery',
    headline: 'Slot-based delivery & dense routes for',
    headlineHighlight: 'fresh retail',
    description:
      'Run same-day and scheduled grocery delivery with time slots, substitution workflows, and AI routing tuned for high stop density — without losing cold-chain discipline.',
    heroImage: '/images/geofleet-in-action-branded-tracking.png',
    color: '#059669',
    colorLight: '#D1FAE5',
    heroKeyPoints: [
      'Customer delivery windows and slot capacity you can trust',
      'AI batches orders by zone for fewer miles per drop',
      'Branded tracking with live ETA for every basket',
      'Temperature-aware sequencing for chilled and frozen lines',
    ],
    stats: [
      { value: '35%', label: 'More drops per route' },
      { value: '96%', label: 'Slot on-time rate' },
      { value: '50%', label: 'Fewer WISMO calls' },
    ],
    painPoints: [
      { icon: 'ri-time-line', title: 'Slot promises break under volume', description: 'Peak hours overwhelm static routes — customers miss windows and churn to competitors.' },
      { icon: 'ri-exchange-line', title: 'Substitutions erode trust', description: 'Without clear comms, out-of-stock swaps drive refunds and one-star reviews.' },
      { icon: 'ri-temp-cold-line', title: 'Cold chain in the last mile', description: 'Mixed ambient, chilled, and frozen on one run needs sequencing — not guesswork.' },
    ],
    features: [
      { icon: 'ri-calendar-check-line', title: 'Slot & capacity engine', description: 'Offer bookable windows that respect fleet size, drive times, and prep cutoffs.', tag: 'AI' },
      { icon: 'ri-route-line', title: 'Density-first routing', description: 'Optimize for stops per hour and on-time slots — not just shortest path.', tag: 'AI' },
      { icon: 'ri-notification-3-line', title: 'Substitution messaging', description: 'Notify customers before the driver leaves the store with clear approve/decline flows.' },
      { icon: 'ri-map-pin-user-line', title: 'Branded live tracking', description: 'Store-branded tracking with narrowing ETAs and handoff instructions.' },
      { icon: 'ri-temp-cold-line', title: 'Temperature sequencing', description: 'Order stops so frozen and chilled items spend minimal time off-vehicle.' },
      { icon: 'ri-smartphone-line', title: 'Driver app & POD', description: 'Scan at pickup, photo at door, age-gate where regulations require.' },
      { icon: 'ri-bar-chart-box-line', title: 'Ops analytics', description: 'Slot fill rate, cost per drop, and exception heatmaps by store and daypart.' },
      { icon: 'ri-plug-line', title: 'POS & OMS sync', description: 'Connect e-commerce, order management, and in-store systems for one live plan.' },
    ],
    testimonial: {
      quote: 'We finally hit our slot promise rate without hiring another wave of planners. Geofleet batches by neighborhood and keeps chilled picks last.',
      role: 'Head of E-commerce',
      metric: '96% slot on-time',
    },
    useCases: [
      'Supermarket same-day delivery',
      'Hypermarket scheduled slots',
      'Dark store / quick-commerce',
      'Regional grocery chains',
      'Meal-kit style bundled runs',
      'B2B store replenishment vans',
    ],
  },

  'b2b-distribution': {
    slug: 'b2b-distribution',
    icon: 'ri-stack-line',
    name: 'B2B & Wholesale Distribution',
    tagline: 'Built for Distribution',
    headline: 'Distribution delivery software for',
    headlineHighlight: 'warehouse to customer',
    description:
      'Plan multi-stop runs from DC to retailers and job sites with proof of delivery, dock scheduling, and analytics built for ops and finance — not spreadsheets.',
    heroImage: '/images/enterprise-visibility.png',
    color: '#2563EB',
    colorLight: '#DBEAFE',
    heroKeyPoints: [
      'Multi-stop routes with pallet and SKU awareness',
      'Digital POD with signatures, photos, and timestamps',
      'Shipper-style visibility for your key accounts',
      'Cost-to-serve and cost-per-drop reporting',
    ],
    stats: [
      { value: '22%', label: 'Lower cost per drop' },
      { value: '99%', label: 'POD capture rate' },
      { value: '2×', label: 'Stops per shift' },
    ],
    painPoints: [
      { icon: 'ri-file-list-3-line', title: 'Dispatch lives in spreadsheets', description: 'Planners email PDFs while drivers improvise — nobody has one version of the day.' },
      { icon: 'ri-building-line', title: 'Dock and window chaos', description: 'Missed appointment slots at retailers and sites cascade into fees and lost trust.' },
      { icon: 'ri-money-dollar-circle-line', title: 'No real unit economics', description: 'You cannot tie fuel, time, and returns to customer or lane profitability.' },
    ],
    features: [
      { icon: 'ri-truck-line', title: 'Load-aware routing', description: 'Sequence stops by weight, cube, and vehicle type with axle-friendly paths.', tag: 'AI' },
      { icon: 'ri-calendar-line', title: 'Appointment windows', description: 'Book retailer and site arrivals with buffers and automatic delay alerts.' },
      { icon: 'ri-camera-line', title: 'Enterprise POD', description: 'Signature, photo, and item-level confirmation for disputes and audits.' },
      { icon: 'ri-user-voice-line', title: 'Account portals', description: 'Let large customers see live status and documents without calling dispatch.' },
      { icon: 'ri-line-chart-line', title: 'Lane analytics', description: 'Compare cost per km, dwell, and on-time by route, hub, and customer tier.' },
      { icon: 'ri-alarm-warning-line', title: 'Exception workflows', description: 'Short pays, refused loads, and redelivery paths with clear ownership.' },
      { icon: 'ri-code-s-slash-line', title: 'ERP & WMS hooks', description: 'Orders and POD events flow back to your system of record.' },
      { icon: 'ri-dashboard-3-line', title: 'Control tower', description: 'One map for active trucks, late risks, and open exceptions.' },
    ],
    testimonial: {
      quote: 'Finance finally trusts our delivery cost numbers. POD is automatic and our top accounts stopped chasing us for ETAs.',
      role: 'VP Operations',
      metric: '22% lower cost/drop',
    },
    useCases: [
      'Foodservice redistribution',
      'Beverage distributors',
      'Industrial supplies vans',
      'Paper and packaging delivery',
      'Regional DC outbound',
      'Cash-and-carry last leg',
    ],
  },

  'medical-supplies': {
    slug: 'medical-supplies',
    icon: 'ri-test-tube-line',
    name: 'Medical Supplies & Labs',
    tagline: 'Built for Med Logistics',
    headline: 'Specimen, kit & supply logistics with',
    headlineHighlight: 'time windows & proof',
    description:
      'Coordinate pickups and deliveries for labs, clinics, and med-surgical supply with strict windows, chain-of-custody style tracking, and auditable proof — adjacent to pharmacy, tuned for B2B medical flow.',
    heroImage: '/images/proof-of-delivery-feature.png',
    color: '#0D9488',
    colorLight: '#CCFBF1',
    heroKeyPoints: [
      'Time-critical pickup and drop-off scheduling',
      'Driver checklists for kit integrity and temperature where needed',
      'Full delivery trace for auditors and partners',
      'Customer notifications with live ETA',
    ],
    stats: [
      { value: '99.2%', label: 'Window compliance' },
      { value: '100%', label: 'Digital trail' },
      { value: '30%', label: 'Fewer redispatches' },
    ],
    painPoints: [
      { icon: 'ri-timer-line', title: 'Lab pickup SLAs', description: 'Missed specimen windows invalidate runs — manual calling cannot scale.' },
      { icon: 'ri-shield-check-line', title: 'Audit and liability', description: 'Without timestamps and signatures, investigations become finger-pointing.' },
      { icon: 'ri-map-pin-line', title: 'Multi-site complexity', description: 'Hospitals, clinics, and depots each have different rules and dock flows.' },
    ],
    features: [
      { icon: 'ri-time-line', title: 'Window routing', description: 'Hard and soft time constraints per stop for lab and clinical schedules.', tag: 'AI' },
      { icon: 'ri-file-shield-line', title: 'Chain-of-custody style POD', description: 'Scan, seal ID, signature, and photo patterns you can defend in review.' },
      { icon: 'ri-temp-cold-line', title: 'Cold awareness', description: 'Flag and sequence temperature-sensitive legs with max time-in-transit rules.' },
      { icon: 'ri-notification-3-line', title: 'Stakeholder alerts', description: 'Notify lab and clinic contacts on pickup, delay, and delivery complete.' },
      { icon: 'ri-route-line', title: 'Pickup + delivery loops', description: 'Optimize combined specimen pickup and supply drop routes in one plan.', tag: 'AI' },
      { icon: 'ri-smartphone-line', title: 'Driver workflows', description: 'Step-by-step tasks so new drivers follow the same compliant path.' },
      { icon: 'ri-bar-chart-box-line', title: 'Compliance analytics', description: 'On-time pickup %, exception reasons, and dwell by facility.' },
      { icon: 'ri-plug-line', title: 'LIS / EMR adjacent', description: 'Integrate order and status feeds from common healthcare and lab stacks.' },
    ],
    testimonial: {
      quote: 'Our lab partners see pickup and delivery timestamps in one place. Redispatches dropped sharply once windows were enforced automatically.',
      role: 'Logistics Director',
      metric: '99.2% on window',
    },
    useCases: [
      'Diagnostic specimen courier',
      'Med-surgical stock delivery',
      'Home health supply drops',
      'Clinical trial kit logistics',
      'Dental and imaging supplies',
      'Biotech cold-chain legs',
    ],
  },

  'field-service': {
    slug: 'field-service',
    icon: 'ri-tools-line',
    name: 'Field Service',
    tagline: 'Built for Field Service',
    headline: 'Appointments, parts & trucks in',
    headlineHighlight: 'one dispatch stack',
    description:
      'Give HVAC, facilities, telco, and industrial field teams optimized daily boards — skills, parts, SLAs, and customer ETAs in sync with your CRM or FSM workflow.',
    heroImage: '/images/geofleet-in-action-operational-control.png',
    color: '#EA580C',
    colorLight: '#FFEDD5',
    heroKeyPoints: [
      'Skill and certification matching for jobs',
      'Parts-on-truck awareness for first-time fix',
      'Customer ETAs that shrink as the day unfolds',
      'Photo and signature proof for every work order',
    ],
    stats: [
      { value: '18%', label: 'More jobs / tech / day' },
      { value: '92%', label: 'First-visit success' },
      { value: '40%', label: 'Fewer "where are you?" calls' },
    ],
    painPoints: [
      { icon: 'ri-user-settings-line', title: 'Wrong tech on site', description: 'Dispatch forgets certifications or tools — you burn a second truck roll.' },
      { icon: 'ri-tools-line', title: 'Parts not on the van', description: 'The job is scheduled but inventory was never tied to the route plan.' },
      { icon: 'ri-phone-line', title: 'Customer anxiety', description: 'Narrow appointment bands without live ETA drive no-shows and complaints.' },
    ],
    features: [
      { icon: 'ri-user-star-line', title: 'Skills-based assignment', description: 'Match work orders to technicians by skill, region, and shift.', tag: 'AI' },
      { icon: 'ri-inbox-line', title: 'Parts linkage', description: 'Reserve or stage parts to routes so techs do not arrive empty-handed.' },
      { icon: 'ri-time-line', title: 'Dynamic ETAs', description: 'Update homeowner and property manager ETAs as prior jobs slip or finish early.', tag: 'AI' },
      { icon: 'ri-map-pin-user-line', title: 'Live map for dispatch', description: 'See every tech, job status, and late risk on one screen.' },
      { icon: 'ri-camera-line', title: 'Work order POD', description: 'Before/after photos, signatures, and notes attached to the stop.' },
      { icon: 'ri-calendar-line', title: 'Appointment optimization', description: 'Cluster jobs by geography while respecting promised bands.' },
      { icon: 'ri-bar-chart-box-line', title: 'Utilization metrics', description: 'Billable hours, travel ratio, and SLA attainment by team.' },
      { icon: 'ri-plug-line', title: 'CRM / FSM ready', description: 'Sync jobs, status, and completion payloads to your stack.' },
    ],
    testimonial: {
      quote: 'We stopped double-booking senior techs and cut "part not on truck" returns by routing inventory with the board.',
      role: 'Service Operations',
      metric: '18% more jobs/day',
    },
    useCases: [
      'HVAC install and maintenance',
      'Facilities and building maintenance',
      'Telco field technicians',
      'Industrial equipment service',
      'Smart home / security installs',
      'Pest and property services',
    ],
  },

  'building-supplies': {
    slug: 'building-supplies',
    icon: 'ri-hammer-line',
    name: 'Building Supplies & Bulky Goods',
    tagline: 'Built for Bulky Delivery',
    headline: 'Heavy, bulky runs with',
    headlineHighlight: 'windows & proof',
    description:
      'Handle lumber, doors, bagged goods, and mixed construction drops with vehicle-appropriate routing, delivery windows, and photo POD that stand up to jobsite disputes.',
    heroImage: '/images/geofleet-hero-routes.png',
    color: '#B45309',
    colorLight: '#FEF3C7',
    heroKeyPoints: [
      'Truck-class routing with realistic service times',
      'Site contact and gate-code capture on the stop',
      'Photo documentation for damage and placement',
      'Bulk order grouping by geography and crane/forklift rules',
    ],
    stats: [
      { value: '25%', label: 'Fewer redeliveries' },
      { value: '95%', label: 'Window hit rate' },
      { value: '20%', label: 'Fuel per tonne ↓' },
    ],
    painPoints: [
      { icon: 'ri-truck-line', title: 'Wrong vehicle for the load', description: 'Flatbed vs box truck mistakes mean failed deliveries and overtime.' },
      { icon: 'ri-map-pin-line', title: 'Jobsite access chaos', description: 'Drivers arrive without gate codes, contacts, or unload instructions.' },
      { icon: 'ri-image-line', title: 'Damage disputes', description: 'Without photos, "it arrived like that" fights eat margin.' },
    ],
    features: [
      { icon: 'ri-truck-line', title: 'Vehicle profiles', description: 'Route by capacity, height restrictions, and unload time per SKU class.', tag: 'AI' },
      { icon: 'ri-time-line', title: 'Construction windows', description: 'Book morning-only or crane-assisted slots with buffers.' },
      { icon: 'ri-user-location-line', title: 'Site instructions', description: 'Capture contacts, codes, and hazards visible in the driver app.' },
      { icon: 'ri-camera-line', title: 'Unload POD', description: 'Photo series and signature tied to order line and timestamp.' },
      { icon: 'ri-route-line', title: 'Multi-drop heavy routes', description: 'Sequence bulky stops to minimize reload and backtrack.', tag: 'AI' },
      { icon: 'ri-alarm-warning-line', title: 'Exception paths', description: 'Refused drop, partial, and redelivery workflows with dispatch alerts.' },
      { icon: 'ri-bar-chart-box-line', title: 'Ton-mile analytics', description: 'Cost and time per tonne by branch and customer type.' },
      { icon: 'ri-plug-line', title: 'ERP order sync', description: 'Pull open deliveries and push status back to your order hub.' },
    ],
    testimonial: {
      quote: 'Photo POD at the gate cut our "not my damage" claims in half. Drivers finally see crane windows before they leave the yard.',
      role: 'Transport Manager',
      metric: '25% fewer redeliveries',
    },
    useCases: [
      'Lumber and building centres',
      'Door and window suppliers',
      'Bagged cement and aggregates',
      'Roofing and insulation drops',
      'Tool rental delivery',
      'Pro desk contractor runs',
    ],
  },

  'automotive-parts': {
    slug: 'automotive-parts',
    icon: 'ri-steering-2-line',
    name: 'Automotive Parts',
    tagline: 'Built for Parts Delivery',
    headline: 'Same-day and dealer-line parts with',
    headlineHighlight: 'priority routing',
    description:
      'Support urgent dealer, workshop, and retail parts runs with priority flags, cutoffs, and tracking tuned for VOR and service-level promises.',
    heroImage: '/images/route-planner-feature.png',
    color: '#DC2626',
    colorLight: '#FEE2E2',
    heroKeyPoints: [
      'VOR and rush order priority in the solver',
      'Dealer and workshop time commitments',
      'Scan-based verify at pickup and handoff',
      'After-hours and Saturday board support',
    ],
    stats: [
      { value: '45 min', label: 'Avg urgent leg' },
      { value: '98%', label: 'Dealer on-time' },
      { value: '15%', label: 'Miles saved' },
    ],
    painPoints: [
      { icon: 'ri-alarm-warning-line', title: 'VOR panic dispatch', description: 'Every urgent job interrupts the plan — planners rebuild routes by hand.' },
      { icon: 'ri-error-warning-line', title: 'Wrong part at dock', description: 'Mis-picks only surface at the dealer — credibility takes the hit.' },
      { icon: 'ri-time-line', title: 'Cutoff creep', description: 'Promised "before noon" slips when the morning board is opaque.' },
    ],
    features: [
      { icon: 'ri-flag-line', title: 'Priority tiers', description: 'VOR, same-day, and standard lanes with solver weighting.', tag: 'AI' },
      { icon: 'ri-barcode-line', title: 'Scan verify', description: 'Confirm SKU and bin at pickup; scan again at dealer receipt.' },
      { icon: 'ri-route-line', title: 'Intracity optimization', description: 'Dense dealer loops with minimal deadhead between workshops.', tag: 'AI' },
      { icon: 'ri-notification-3-line', title: 'Dealer comms', description: 'SMS and email with live ETA and delay reasons.' },
      { icon: 'ri-map-pin-user-line', title: 'Branded tracking', description: 'OEM or distributor-branded pages for premium accounts.' },
      { icon: 'ri-camera-line', title: 'Exception POD', description: 'Document refused or partial deliveries with photos.' },
      { icon: 'ri-bar-chart-box-line', title: 'SLA dashboards', description: 'On-time by dealer tier, SKU class, and driver.' },
      { icon: 'ri-plug-line', title: 'DMS / IMS hooks', description: 'Connect parts orders and acknowledgements to your inventory brain.' },
    ],
    testimonial: {
      quote: 'VOR orders slot in without wrecking the whole board. Dealers stopped calling — they watch the live ETA instead.',
      role: 'Network Logistics',
      metric: '98% dealer on-time',
    },
    useCases: [
      'OEM dealer overnight programs',
      'Independent workshop supply',
      'Tire wholesale vans',
      'Accessory same-day',
      'Collision parts rush',
      'Fleet maintenance depots',
    ],
  },

  'contract-logistics': {
    slug: 'contract-logistics',
    icon: 'ri-ship-line',
    name: '3PL & Contract Logistics',
    tagline: 'Built for 3PL',
    headline: 'Multi-customer logistics with',
    headlineHighlight: 'portals & SLAs',
    description:
      'Run dedicated and shared transport for multiple shippers with segregated visibility, carrier-style ETAs, and analytics your customers can trust — beyond generic courier tools.',
    heroImage: '/images/geofleet-in-action-control-tower.png',
    color: '#4338CA',
    colorLight: '#E0E7FF',
    heroKeyPoints: [
      'Per-shipper branded tracking and SLAs',
      'Hub and linehaul aware planning where you need it',
      'Exception and claim workflows with audit trail',
      'Margin and cost-to-serve by account',
    ],
    stats: [
      { value: '30%', label: 'Less status admin' },
      { value: '99%', label: 'Tracking adoption' },
      { value: '2×', label: 'Faster onboarding' },
    ],
    painPoints: [
      { icon: 'ri-group-line', title: 'Customer visibility overload', description: 'Ops answers the same "where is it?" question across dozens of accounts.' },
      { icon: 'ri-scales-3-line', title: 'SLA ambiguity', description: 'Contracts promise service levels your tools cannot measure or prove.' },
      { icon: 'ri-line-chart-line', title: 'Margin blind spots', description: 'You win volume but lose money on lanes you never modeled per customer.' },
    ],
    features: [
      { icon: 'ri-building-4-line', title: 'Multi-tenant visibility', description: 'Each shipper sees only their freight, branded to them or to you.' },
      { icon: 'ri-truck-line', title: 'Network routing', description: 'Optimize across hubs, linehaul, and last mile with role-based views.', tag: 'AI' },
      { icon: 'ri-alarm-warning-line', title: 'SLA monitoring', description: 'Alerts before breaches with suggested recovery actions.' },
      { icon: 'ri-file-list-3-line', title: 'Audit-ready events', description: 'Timestamped status, POD, and comms for every leg.' },
      { icon: 'ri-money-dollar-circle-line', title: 'Account economics', description: 'Revenue, cost, and margin signals per customer and lane.' },
      { icon: 'ri-user-voice-line', title: 'Shipper self-serve', description: 'Booking, documents, and live map without email ping-pong.' },
      { icon: 'ri-bar-chart-box-line', title: 'Executive scorecards', description: 'On-time, cost per order, and quality by major contract.' },
      { icon: 'ri-code-s-slash-line', title: 'API-first', description: 'Inject orders and push events to TMS, WMS, and shipper systems.' },
    ],
    testimonial: {
      quote: 'Our top shipper asked for branded tracking — we turned it on per account in a day. Status tickets dropped overnight.',
      role: 'COO',
      metric: '30% less status work',
    },
    useCases: [
      'Regional contract transport',
      'E-commerce fulfillment 3PL',
      'Industrial dedicated fleet',
      'Cold chain 3PL legs',
      'Retail inbound consolidation',
      'White-label last mile',
    ],
  },
};
